import type { AwardCardView, MatchupSummary, ScoreboardView } from '@fsp/domain';
import { useAwards, useScoreboard } from '../data/hooks';
import { useViewedPeriod } from '../data/period';
import { FromLeague } from './shared/FromLeague';
import { LiveDot } from '../kit';
import { Panel } from '../kit/Panel';
import { useTier } from '../shell/useTier';
import { StatsNote } from './shared/StatsNote';
import { Line, Stack, Text, Title } from '../kit/ui';
import { Detail, useAtLeast } from '../data/detail';
import { useActiveLeague } from '../data/LeaguesProvider';
import { FieldTable } from './gameDay/FieldTable';
import { GameGrid } from './scoreboard/GameGrid';
import { WeekScores, weekScoresShown } from './scoreboard/WeekScores';
import { AwardsRace } from './scoreboard/AwardsRace';
import '../styles/screens/league-screens.css';
import '../styles/screens/scoreboard.css';

// Drawn through the kit (LOOKS.md Part 7).
//
// THE WEEK AT A GLANCE (B-385): the games, my own first, in a grid that takes
// any number of them at any width; beside them on a desktop, under them on a
// phone, the week's column — every score in order, and the awards as they
// stand. It was a grid of wide cards and nothing else, which on a big screen
// was a row and a half of cards over an empty page.
//
// Split for the reason ScheduleScreen is (E12): `useAwards` is keyed on the
// period this view carries, so the body cannot run before the view exists.
export function ScoreboardScreen() {
  return (
    // The week on screen (D4, B-216); now when the address names none.
    <FromLeague snapshot={useScoreboard(useViewedPeriod().period)}>
      {(view) => <ScoreboardBody view={view} />}
    </FromLeague>
  );
}

function ScoreboardBody({ view }: { view: ScoreboardView }) {
  const awards = useAwards(view.period.index);
  const tier = useTier();
  const standard = useAtLeast('standard');
  // LIVE ONLY WHILE THE NUMBERS ARE (BACKLOG L2). This read a matchup's
  // status, so a week between Thursday's game and Sunday's pulsed "Live" over
  // scores that move once a night (round 3, N5).
  const live = view.stats.state === 'live';
  const allPlay = useActiveLeague()?.league.format.id === 'all-play';
  const games = view.matchups;
  const settled = games.length > 0 && games.every((m) => m.status === 'final');
  // HOW MUCH OF THE WEEK IS LEFT, across the league: the sum of every side's
  // starters still to play, from Standard (DETAIL_LEVELS §Scoreboard).
  const toPlay = games
    .filter((m) => m.status !== 'final')
    .reduce((n, m) => n + m.home.yetToPlay + m.away.yetToPlay, 0);
  const race = awards?.perPeriod ?? [];
  const showScores = view.field === null && weekScoresShown(games, standard);
  const showRace = race.some((a) => a.winners.length > 0);
  const side = showScores || showRace;
  return (
    <div className="fsp-screen fsp-flow">
      <div className="fsp-pinned">
        <Stack gap={1}>
          <Line spread>
            <Title>{view.period.label} scoreboard</Title>
            {live ? <LiveDot label="Live" /> : null}
          </Line>
          <StatsNote stats={view.stats} />
          {toPlay > 0 ? (
            <Detail min="standard">
              <Text size="xs" tone="secondary">
                {toPlay} starter{toPlay === 1 ? '' : 's'} yet to play across the league
              </Text>
            </Detail>
          ) : null}
        </Stack>
      </div>
      <div className={`fsp-duo fsp-duo-${tier}${side ? '' : ' fsp-duo-alone'}`}>
        <div className="fsp-duo-main">
          {view.field !== null ? (
            // A WEEK AGAINST THE FIELD (Q4, Q5, B-360, B-361): every team's
            // score in order is the board, in place of the games.
            <Panel title="Scores in order">
              <FieldTable rows={view.field} allPlay={allPlay} />
              {/* WHO IS OUT ALREADY (Q1, B-364), latest first. */}
              {view.out !== null && view.out.length > 0 ? (
                <div className="fsp-sub fsp-ui-wrap-text fsp-field-gone">
                  Out: {view.out.map((o) => `${o.team.name} (Week ${o.period})`).join(', ')}
                </div>
              ) : null}
            </Panel>
          ) : (
            <Games games={games} awards={race} view={view} big={tier === 'large'} />
          )}
        </div>
        {side ? (
          <div className="fsp-duo-side">
            {showScores ? <WeekScores matchups={games} median={view.median} /> : null}
            {showRace ? (
              <AwardsRace
                awards={race}
                period={view.period.index}
                final={awards?.status === 'final' || settled}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The week's games in a panel: mine first, the grid's (`GameGrid`). */
function Games({
  games,
  awards,
  view,
  big,
}: {
  games: MatchupSummary[];
  awards: AwardCardView[];
  view: ScoreboardView;
  /** A large screen: bigger type, a board to read from across a room (DESIGN.md §4). */
  big: boolean;
}) {
  const standard = useAtLeast('standard');
  return (
    <Panel
      title="Games"
      meta={
        // A count, so from Standard.
        games.length > 0 && standard ? (
          <span className="fsp-muted fsp-sb-count">
            {games.length} game{games.length === 1 ? '' : 's'}
          </span>
        ) : null
      }
    >
      <GameGrid games={games} awards={awards} stats={view.stats} big={big} />
    </Panel>
  );
}
